<template>
  <v-dialog v-model="visible" max-width="600">
    <v-card>
      <v-card-title>
        {{ dialogTitle }}
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <v-form @submit.prevent="submit">
          <v-row>
            <template v-if="showScheduleFields">
              <v-col cols="12" md="6">
                <v-text-field label="Appointment Date" v-model="form.date" type="date" :min="todayDate" required
                  @input="logDateSelection" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field label="Start Time" v-model="form.startTime" type="time" required :min="minStartTime"
                  @change="updateEndTime" @input="logTimeSelection" />
              </v-col>
            </template>
            <v-col cols="12">
              <label>{{ mode === 'approve' ? 'Lesson hours (from student request)' : 'Select Lesson Hours' }}</label>
              <v-radio-group v-model="form.lessonHours" row @change="updateEndTime">
                <v-radio label="1 Hour" :value="1" />
                <v-radio label="2 Hours" :value="2" :disabled="remainingHours < 2 && !paymentMethodAdded" />
              </v-radio-group>
              <v-alert v-if="remainingHours < 2 && !paymentMethodAdded" type="info" dense class="mt-2">
                You only have {{ remainingHours }} booking hour(s) remaining. Please add a payment method to book
                2-hour lessons.
              </v-alert>
            </v-col>
            <v-col cols="12">
              <v-textarea :label="mode === 'approve' ? 'Notes (from student)' : 'Notes'" v-model="form.notes"
                rows="3" />
            </v-col>
          </v-row>
          <v-btn :loading="isSubmitting" :disabled="isSubmitting" type="submit" color="primary" class="mb-3">
            {{ submitLabel }}
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'BookingModal',
  props: {
    value: Boolean,
    appointment: Object,
    remainingHours: { type: Number, default: 0 },
    paymentMethodAdded: { type: Boolean, default: false },
    mode: {
      type: String,
      default: 'book' // 'book' | 'request' | 'approve'
    }
  },
  data() {
    return {
      visible: this.value,
      isSubmitting: false,
      form: {
        date: '',
        startTime: '',
        endTime: '',
        parking: false,
        highwayDriving: false,
        innerCityDrivingTurns: false,
        lessonHours: 1,
        notes: '',
        paymentMethod: 'mockRoadTestG2'
      }
    }
  },
  computed: {
    showScheduleFields() {
      return this.mode !== 'request'
    },
    dialogTitle() {
      if (this.mode === 'request') return 'Request Booking'
      if (this.mode === 'approve') return 'Approve booking'
      return 'Add New Booking'
    },
    submitLabel() {
      if (this.mode === 'request') return 'Request Booking'
      if (this.mode === 'approve') return 'Approve & schedule'
      return 'Book Appointment'
    },
    todayDate() {
      const today = new Date()
      return today.toISOString().split('T')[0]
    },
    minStartTime() {
      if (this.form.date === this.todayDate) {
        const now = new Date()
        now.setSeconds(0, 0)
        return now.toTimeString().slice(0, 5)
      }
      return '00:00'
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.visible = val
        if (val && this.mode === 'approve') {
          this.$nextTick(() => this.hydrateFromAppointment())
        }
      }
    },
    visible(val) {
      this.$emit('input', val)
    },
    mode(val) {
      if (val === 'approve' && this.visible) {
        this.$nextTick(() => this.hydrateFromAppointment())
      }
    },
    appointment: {
      deep: true,
      handler() {
        if (this.visible && this.mode === 'approve') {
          this.$nextTick(() => this.hydrateFromAppointment())
        }
      }
    },
    'form.startTime'(newTime) {
      if (this.form.date === this.todayDate && newTime < this.minStartTime) {
        this.form.startTime = this.minStartTime
      }
    }
  },
  mounted() {
    if (this.visible && this.mode === 'approve') {
      this.$nextTick(() => this.hydrateFromAppointment())
    }
  },
  methods: {
    hydrateFromAppointment() {
      if (this.mode !== 'approve') return
      const a = this.appointment || {}
      const hours = Number(a.hoursLesson)
      this.form.lessonHours = hours === 2 ? 2 : 1
      this.form.notes = a.notes == null ? '' : String(a.notes)

      const start = a.start ? new Date(a.start) : null
      if (start && !isNaN(start.getTime())) {
        if (this.$moment) {
          this.form.date = this.$moment(start).format('YYYY-MM-DD')
          this.form.startTime = this.$moment(start).format('HH:mm')
        } else {
          const pad = (n) => String(n).padStart(2, '0')
          this.form.date = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`
          this.form.startTime = `${pad(start.getHours())}:${pad(start.getMinutes())}`
        }
      } else {
        this.form.date = ''
        this.form.startTime = ''
        this.form.endTime = ''
      }
      this.updateEndTime()
    },
    logDateSelection() {
      console.log('📅 DATE SELECTED:', this.form.date)
    },
    logTimeSelection() {
      console.log('🕐 TIME SELECTED:', this.form.startTime)
    },
    close() {
      this.visible = false
    },
    updateEndTime() {
      if (!this.form.startTime) {
        this.form.endTime = ''
        return
      }
      const [h, m] = this.form.startTime.split(':').map(Number)
      const lessonHours = Number(this.form.lessonHours || 1)
      const endDate = new Date(0, 0, 0, h + lessonHours, m)
      this.form.endTime = endDate.toTimeString().slice(0, 5)
    },
    async submit() {
      if (this.form.lessonHours > this.remainingHours && !this.paymentMethodAdded) {
        this.$emit('error', `You only have ${this.remainingHours} free hour(s) remaining.`)
        return
      }
      this.isSubmitting = true
      try {
        await this.$emit('submit', { ...this.form })
      } catch (e) {
        console.error('Submit failed', e)
      } finally {
        this.isSubmitting = false
        this.close()
      }
    }
  }
}
</script>